/**
 * Facility booking blackout dates. Keep in sync with
 * backend/src/utils/bookingBlackoutDates.ts
 */

/** 2nd anniversary — free walk-in day; online lane/cage bookings closed. */
export const ANNIVERSARY_WALK_IN_YMD = "2026-08-11";

/** Halloween — free walk-in day; online lane/cage bookings closed. */
export const HALLOWEEN_WALK_IN_YMD = "2026-10-31";

/** Thanksgiving Monday — facility closed; online lane/cage bookings closed. */
export const THANKSGIVING_YMD = "2026-10-12";

const CLOSED_BOOKING_DATES = new Set<string>([
  "2025-12-24",
  "2025-12-25",
  "2025-12-26",
  "2025-12-31",
  "2026-01-01",
  THANKSGIVING_YMD,
  ANNIVERSARY_WALK_IN_YMD,
  HALLOWEEN_WALK_IN_YMD,
]);

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const normalizeToDateKey = (input: string | Date | null | undefined): string | null => {
  if (!input) return null;
  try {
    if (input instanceof Date) {
      if (Number.isNaN(input.getTime())) return null;
      return `${input.getFullYear()}-${pad(input.getMonth() + 1)}-${pad(input.getDate())}`;
    }
    const base = String(input).split("T")[0];
    const parts = base.split("-");
    if (parts.length === 3) {
      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        return `${year}-${pad(month)}-${pad(day)}`;
      }
    }
    const parsed = new Date(input);
    if (Number.isNaN(parsed.getTime())) return null;
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  } catch {
    return null;
  }
};

/** Dates for react-day-picker `disabled.dates`. */
export const getBookingBlackoutDateObjects = (): Date[] =>
  Array.from(CLOSED_BOOKING_DATES).map((ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d);
  });

export const isBookingBlackoutDate = (
  input: string | Date | null | undefined
): boolean => {
  const key = normalizeToDateKey(input);
  if (!key) return false;
  return CLOSED_BOOKING_DATES.has(key);
};

export const isAnniversaryWalkInDay = (
  input: string | Date | null | undefined
): boolean => normalizeToDateKey(input) === ANNIVERSARY_WALK_IN_YMD;

export const isHalloweenWalkInDay = (
  input: string | Date | null | undefined
): boolean => normalizeToDateKey(input) === HALLOWEEN_WALK_IN_YMD;

export const isThanksgivingDay = (
  input: string | Date | null | undefined
): boolean => normalizeToDateKey(input) === THANKSGIVING_YMD;

export const getBookingBlackoutTitle = (
  input: string | Date | null | undefined
): string => {
  if (isAnniversaryWalkInDay(input)) {
    return "It's our 2nd anniversary!";
  }
  if (isHalloweenWalkInDay(input)) {
    return "Happy Halloween!";
  }
  if (isThanksgivingDay(input)) {
    return "Closed for Thanksgiving";
  }
  return "Bookings are closed on this date. Happy Holidays!";
};

export const getBookingBlackoutBody = (
  input: string | Date | null | undefined
): string => {
  if (isAnniversaryWalkInDay(input)) {
    return (
      "As appreciation, walk-ins are free of cost today. " +
      "Online bookings for lanes and cages are closed on August 11 — please choose another date."
    );
  }
  if (isHalloweenWalkInDay(input)) {
    return (
      "Walk-ins are free of cost today. " +
      "Online bookings for lanes and cages are closed on October 31 — please choose another date."
    );
  }
  if (isThanksgivingDay(input)) {
    return (
      "We're closed on Monday, October 12. " +
      "Online bookings for lanes and cages are not available — please choose another date."
    );
  }
  return "Please choose another date to see available times and book your slot.";
};
